import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Stack,
  Button,
  alpha,
  useTheme,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import TableRowsOutlinedIcon from "@mui/icons-material/TableRowsOutlined";
import {
  getCommonTableDisplayConfig,
  shouldUseLargeCommonTable,
} from "../../utils/responsiveLayoutSettings";

const EmptyState = ({ message, onAdd }) => (
  <Stack
    spacing={2}
    alignItems='center'
    justifyContent='center'
    sx={{
      py: 6,
      px: 3,
      minHeight: 250,
      bgcolor: (theme) => alpha(theme.palette.background.paper, 0.6),
      borderRadius: 2,
      border: "1px dashed",
      borderColor: (theme) => alpha(theme.palette.primary.main, 0.2),
      m: 2,
    }}
  >
    <TableRowsOutlinedIcon
      sx={{
        fontSize: 48,
        color: (theme) => alpha(theme.palette.primary.main, 0.3),
        mb: 1,
      }}
    />
    <Typography
      variant='body1'
      color='text.secondary'
      align='center'
      sx={{
        maxWidth: 300,
        mb: 1,
      }}
    >
      {message}
    </Typography>
    {onAdd && (
      <Button
        variant='outlined'
        color='primary'
        startIcon={<AddCircleOutlineIcon />}
        onClick={onAdd}
        sx={{
          borderRadius: 2,
          textTransform: "none",
          px: 3,
        }}
      >
        Add Row
      </Button>
    )}
  </Stack>
);

export const ReadOnlyCell = ({
  value,
  width = "80px",
  textAlign = "center",
}) => (
  <Box sx={{ width: width }}>
    <Typography
      variant='body2'
      sx={{ fontWeight: 500, color: "text.primary", textAlign: textAlign }}
    >
      {value}
    </Typography>
  </Box>
);

export default function CommonTable({
  rows,
  columns,
  noRowsHint = "No data available yet.",
  onAddRow,
  needHeaderColor = false,
  children,
  tableDisplayConfig,
}) {
  const theme = useTheme();
  const [useLargeLayout, setUseLargeLayout] = useState(() =>
    shouldUseLargeCommonTable()
  );
  const displayConfig = useMemo(
    () => tableDisplayConfig || getCommonTableDisplayConfig(useLargeLayout),
    [tableDisplayConfig, useLargeLayout]
  );
  const controlColumnKeys = displayConfig.controlColumnKeys || [];
  const resolveColumnWidth = (col) =>
    col.responsiveWidth ||
    displayConfig.columns?.[col.key] ||
    col.width ||
    "auto";
  const isControlColumn = (col) =>
    Boolean(col.fixedSize || controlColumnKeys.includes(col.key));
  const hasHeaderGroups = columns.some((col) => col.headerGroup);
  const groupHeaderRef = useRef(null);
  const [groupHeaderHeight, setGroupHeaderHeight] = useState(0);
  const headerRows = useMemo(() => {
    if (!hasHeaderGroups) return [columns];

    const topRow = [];
    columns.forEach((col) => {
      if (!col.headerGroup) {
        topRow.push({ ...col, rowSpan: 2 });
        return;
      }
      const previous = topRow[topRow.length - 1];
      if (previous?.headerGroupKey === col.headerGroup.key) {
        previous.colSpan += 1;
      } else {
        topRow.push({
          key: `group-${col.headerGroup.key}-${topRow.length}`,
          headerGroupKey: col.headerGroup.key,
          label: col.headerGroup.label,
          colSpan: 1,
          headerAlign: "center",
          backgroundColor: col.backgroundColor,
          stickyLeft: col.stickyLeft,
        });
      }
    });
    return [topRow, columns.filter((col) => col.headerGroup)];
  }, [columns, hasHeaderGroups]);

  useLayoutEffect(() => {
    const row = groupHeaderRef.current;
    if (!hasHeaderGroups || !row) return undefined;

    // Keep the second header row below the first at any zoom or font size.
    const measure = () =>
      setGroupHeaderHeight(row.getBoundingClientRect().height);
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(row);
    return () => observer.disconnect();
  }, [hasHeaderGroups, rows?.length, useLargeLayout]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const updateLayoutMode = () => {
      setUseLargeLayout(shouldUseLargeCommonTable());
    };

    window.addEventListener("resize", updateLayoutMode);
    window.visualViewport?.addEventListener("resize", updateLayoutMode);

    return () => {
      window.removeEventListener("resize", updateLayoutMode);
      window.visualViewport?.removeEventListener("resize", updateLayoutMode);
    };
  }, []);

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        backgroundColor: "background.paper",
      }}
    >
      {rows && rows.length > 0 ? (
        <Box>
          <Table
            stickyHeader
            sx={{
              minWidth: displayConfig.tableMinWidth,
            }}
          >
            <TableHead>
              {headerRows.map((headerRow, headerRowIndex) => (
                <TableRow
                  key={headerRowIndex}
                  ref={
                    hasHeaderGroups && headerRowIndex === 0
                      ? groupHeaderRef
                      : null
                  }
                >
                  {headerRow.map((col) => {
                    const width = col.headerGroupKey
                      ? undefined
                      : resolveColumnWidth(col);
                    const isFixedControl = isControlColumn(col);
                    const isStickyLeft = col.stickyLeft !== undefined;

                    return (
                      <TableCell
                        key={col.key}
                        colSpan={col.colSpan}
                        rowSpan={col.rowSpan}
                        scope={col.headerGroupKey ? "colgroup" : "col"}
                        align={col.headerAlign || "center"}
                        sx={{
                          backgroundColor:
                            col.backgroundColor ||
                            theme.palette.background.paper,
                          position: "sticky",
                          top: headerRowIndex === 0 ? 0 : groupHeaderHeight,
                          left: isStickyLeft ? col.stickyLeft : undefined,
                          zIndex: isStickyLeft ? 4 : 2,
                          fontWeight: 600,
                          fontSize: "0.825rem",
                          textTransform: col.headerGroupKey
                            ? "none"
                            : "uppercase",
                          letterSpacing: 0,
                          borderColor: (theme) =>
                            alpha(theme.palette.divider, 0.16),
                          width,
                          minWidth: width,
                          maxWidth: width,
                          px: isFixedControl
                            ? 0
                            : displayConfig.headerCellPaddingX,
                          py: isFixedControl
                            ? 0
                            : col.headerGroupKey
                              ? 0.75
                              : displayConfig.headerCellPaddingY,
                          ...(isStickyLeft
                            ? {
                                boxSizing: "border-box",
                                backgroundColor:
                                  col.backgroundColor ||
                                  theme.palette.background.paper,
                                ...(col.stickyDivider
                                  ? {
                                      boxShadow:
                                        "3px 0 5px -4px rgba(0, 0, 0, 0.35)",
                                    }
                                  : {}),
                              }
                            : {}),
                          ...(isFixedControl
                            ? {
                                height: displayConfig.controlSize,
                                verticalAlign: "middle",
                                "& .MuiCheckbox-root": {
                                  height: displayConfig.controlSize,
                                  p: 0,
                                  width: displayConfig.controlSize,
                                },
                              }
                            : {}),
                        }}
                      >
                        {col.label}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableHead>
            <TableBody>
              {rows.map((row, index) => (
                <TableRow
                  key={`row-${index}`}
                  id={`c_${index}`}
                  sx={{
                    "&:hover": {
                      bgcolor: (theme) =>
                        alpha(theme.palette.primary.main, 0.03),
                    },
                    transition: "background-color 0.2s ease-in-out",
                  }}
                >
                  {columns.map((col) => {
                    const width = resolveColumnWidth(col);
                    const isFixedControl = isControlColumn(col);
                    const isStickyLeft = col.stickyLeft !== undefined;

                    return (
                      <TableCell
                        key={col.key}
                        align={col.colAlign || "center"}
                        sx={{
                          borderBottom: "1px solid",
                          borderColor: (theme) =>
                            alpha(theme.palette.divider, 0.12),
                          color: "text.primary",
                          fontSize: "0.9rem",
                          backgroundColor:
                            col.backgroundColor || "transparent",
                          width,
                          minWidth: width,
                          maxWidth: width,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          position: isStickyLeft ? "sticky" : undefined,
                          left: isStickyLeft ? col.stickyLeft : undefined,
                          zIndex: isStickyLeft ? 1 : undefined,
                          px: isFixedControl
                            ? 0
                            : displayConfig.rowCellPaddingX,
                          py: isFixedControl
                            ? 0
                            : displayConfig.rowCellPaddingY,
                          ...(isStickyLeft
                            ? {
                                boxSizing: "border-box",
                                backgroundColor:
                                  col.backgroundColor ||
                                  theme.palette.background.paper,
                                ...(col.stickyDivider
                                  ? {
                                      boxShadow:
                                        "3px 0 5px -4px rgba(0, 0, 0, 0.35)",
                                    }
                                  : {}),
                              }
                            : {}),
                          ...(isFixedControl
                            ? {
                                height: displayConfig.controlSize,
                                verticalAlign: "middle",
                                "& .MuiCheckbox-root": {
                                  height: displayConfig.controlSize,
                                  p: 0,
                                  width: displayConfig.controlSize,
                                },
                                "& .MuiIconButton-root": {
                                  height: displayConfig.controlSize,
                                  p: 0.5,
                                  width: displayConfig.controlSize,
                                },
                              }
                            : {}),
                        }}
                      >
                        {col.render ? col.render(row, index) : row[col.key]}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
              {children && (
                <TableRow
                  sx={{
                    backgroundColor: (theme) => theme.palette.background.paper,
                    borderTop: "2px solid",
                    borderColor: (theme) => theme.palette.primary.main,
                    "& .MuiTableCell-root": {
                      fontWeight: 600,
                      backgroundColor: (theme) =>
                        alpha(theme.palette.primary.main, 0.05),
                    },
                  }}
                >
                  {children}
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      ) : (
        <EmptyState message={noRowsHint} onAdd={onAddRow} />
      )}
    </TableContainer>
  );
}
